import { useState, useEffect, useCallback, useRef } from 'react';
import api, { BusinessResponse } from '@/lib/api';

export type BusinessSummary = BusinessResponse;

interface UseBusinessesReturn {
  businesses: BusinessSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useBusinesses(): UseBusinessesReturn {
  const [businesses, setBusinesses] = useState<BusinessSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchBusinesses = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await api.listBusinesses();
      if (fetchIdRef.current !== id) return;
      setBusinesses(result.businesses);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load businesses');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchBusinesses();
    return () => {
      fetchIdRef.current++;
    };
  }, [fetchBusinesses]);

  return { businesses, loading, error, refresh: fetchBusinesses };
}
